import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  type BounceKind,
  type SwitchPort,
  useBouncePort,
  useRefreshSwitchPorts,
  useSwitchPorts,
} from '../../../features/locations/use-switch-ports'
import { useIdOrCode } from '../../use-id-or-code'
import { Button, ConfirmDialog, EmptyRow, Inline, SubCrumb, td, th } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards, type Tone } from './ui'

const statusTone = (s: SwitchPort['status']): Tone => (s === 'connected' ? 'ok' : s === 'disabled' ? 'bad' : 'off')

interface Pending {
  port: SwitchPort
  kind: BounceKind
  prompt: string
}

export function PorcelainSwitchPorts() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const ports = useSwitchPorts(idOrCode)
  const bounce = useBouncePort()
  const refreshPorts = useRefreshSwitchPorts()

  const [confirm, setConfirm] = useState<Pending | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const header = (
    <PageHeader
      eyebrow={<SubCrumb idOrCode={idOrCode} section="network" page="Switch ports" />}
      title="Switch ports"
      subtitle="Port status and connected clients. Bounce a port or its PoE to restart a stuck device."
    />
  )

  if (location.isLoading || ports.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={2} />
      </section>
    )
  const storeCode = location.data?.aptosStoreCode ?? location.data?.code ?? null
  if (location.error || ports.error || !ports.data || !storeCode)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load switch ports"
          body={ports.error ? apiErrorMessage(ports.error) : 'Check your connection and try again in a moment.'}
        />
      </section>
    )

  const list = ports.data.ports

  const start = (port: SwitchPort, kind: BounceKind) => {
    setNotice(null)
    bounce.mutate(
      { storeCode, portId: port.portId, kind },
      {
        onSuccess: (r) => {
          if (r.status === 'needs-confirmation') setConfirm({ port, kind, prompt: r.prompt })
          else setNotice(`${kind === 'poe' ? 'PoE' : 'Port'} ${port.portId} bounced.`)
        },
      },
    )
  }

  const confirmed = () => {
    if (!confirm) return
    const { port, kind } = confirm
    bounce.mutate(
      { storeCode, portId: port.portId, kind, confirmed: true },
      {
        onSuccess: () => {
          setNotice(`${kind === 'poe' ? 'PoE' : 'Port'} ${port.portId} bounced.`)
          setConfirm(null)
        },
      },
    )
  }

  const busy = bounce.isPending

  return (
    <section>
      {header}
      <div className="mt-8">
        <Card
          title={`${list.length} port${list.length === 1 ? '' : 's'}`}
          aside={
            <div className="flex items-center gap-3">
              {ports.data.switchVendor ? (
                <span className="text-[12px] text-[var(--color-muted)]">{ports.data.switchVendor}</span>
              ) : null}
              <Button
                variant="quiet"
                disabled={refreshing}
                onClick={async () => {
                  setRefreshing(true)
                  try {
                    await refreshPorts()
                  } finally {
                    setRefreshing(false)
                  }
                }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          }
        >
          {notice || (bounce.error && !confirm) ? (
            <div className="px-5 pt-3">
              {bounce.error && !confirm ? (
                <Inline tone="bad">{apiErrorMessage(bounce.error)}</Inline>
              ) : (
                <Inline tone="ok">{notice}</Inline>
              )}
            </div>
          ) : null}
          {list.length === 0 ? (
            <EmptyRow>No switch ports reported for this store.</EmptyRow>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Port</th>
                    <th className={th}>Status</th>
                    <th className={th}>VLAN</th>
                    <th className={th}>Client</th>
                    <th className={th}>Traffic</th>
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.portId} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={td}>
                        <span className="font-medium" style={mono}>
                          {p.portId}
                        </span>
                        {p.name ? <span className="ml-2 text-[var(--color-muted)]">{p.name}</span> : null}
                      </td>
                      <td className={td}>
                        <Badge label={p.status} tone={statusTone(p.status)} />
                      </td>
                      <td className={td} style={mono}>
                        {p.vlanTag ?? '-'}
                      </td>
                      <td className={td}>
                        {p.clientMac ? (
                          <div className="leading-tight">
                            <div>{p.clientDescription ?? p.clientIp ?? p.clientMac}</div>
                            <div className="text-[12px] text-[var(--color-muted)]" style={mono}>
                              {p.clientMac}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[var(--color-muted)]">-</span>
                        )}
                      </td>
                      <td className={`${td} text-[13px] text-[var(--color-muted)]`}>
                        {p.trafficReceived || p.trafficSent
                          ? `${p.trafficReceived ?? '-'} in / ${p.trafficSent ?? '-'} out`
                          : '-'}
                      </td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          <Button variant="quiet" disabled={busy || !p.enabled} onClick={() => start(p, 'port')}>
                            Bounce
                          </Button>
                          <Button variant="quiet" disabled={busy || !p.enabled} onClick={() => start(p, 'poe')}>
                            Bounce PoE
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={confirm !== null}
        title={`Bounce ${confirm?.kind === 'poe' ? 'PoE on ' : ''}port ${confirm?.port.portId ?? ''}?`}
        body={confirm?.prompt}
        confirmLabel="Yes, bounce"
        danger
        pending={busy}
        error={bounce.error ? apiErrorMessage(bounce.error) : null}
        onConfirm={confirmed}
        onClose={() => {
          bounce.reset()
          setConfirm(null)
        }}
      />
    </section>
  )
}
